import React, { useEffect, useState } from "react";
import Header from "./Header/Header";
import "./AnalyticsScreen.scss";
import BasicReports from "./BasicReports/BasicReports";
import {
  clearBasicAnalytics,
  fetchBasicAnalyticsRequest,
  setSelectedAttemptId,
} from "../../../redux/exam_analytics/analyticsSlice";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { useLocation, useHistory } from "react-router-dom";
import { resolveImmediateReturnPath } from "../../../custom_utilities/dashboardBannerNavigation";
import { resolveAttemptId } from "../utils/resolveAttemptId";
import { MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH } from "../../MistakeBook/mistakeBookRoutes";

const AnalyticsScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    testDetails,
    examSubmitted,
    loading: examLoading,
    error: examError,
  } = useSelector((state) => state.examSlice);
  const source = testDetails?.source;
  const [attempt, setAttempt] = useState(0);

  const { basicAnalytics, error } = useSelector((s) => s.analyticsSlice);
  const location = useLocation();
  const routePaperType = location.state?.paper_type;
  const routeSetId = location.state?.set_id;
  const routePaperId = location.state?.paper_id;
  const routePlannerTestId = location.state?.planner_test_id;
  const isFromSubmit = Boolean(location.state?.from_submit);
  const isMistakeBookPracticeSet =
    source === "mistake_book_practice_set" ||
    location.state?.fromMistakeBookPracticeSet === true ||
    location.pathname === MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH;

  // Only wait while submit is actually in flight (loading). On refresh,
  // redux resets and we fetch with route ids instead of hanging forever.
  const waitingForSubmit =
    isFromSubmit && !examSubmitted && examLoading === true;

  const getRequestPayload = () => {
    if (routeSetId) {
      return {
        paper_type: routePaperType,
        set_id: routeSetId,
      };
    }

    if (routePaperId) {
      return {
        paper_type: routePaperType,
        paper_id: routePaperId,
        planner_test_id: routePlannerTestId,
      };
    }

    return {};
  };

  const handleBack = () => {
    const subjectName =
      location.state?.subjectName ||
      location.state?.subject?.name ||
      testDetails?.subject ||
      testDetails?.subjectName ||
      "";
    const subjectId =
      location.state?.subjectId ||
      location.state?.subject?.id ||
      testDetails?.subjectId ||
      "";
    const mistakeBookReturnState = {
      practiceSetsTab: location.state?.practiceSetsTab || "completed",
      fromMistakeBookPracticeSet: true,
      ...(subjectName
        ? {
            subjectName,
            subjectId,
            subject: { id: subjectId, name: subjectName },
          }
        : {}),
      ...(location.state?.year != null ? { year: location.state.year } : {}),
    };

    const resolvedReturnTo = resolveImmediateReturnPath({
      location,
      source: isMistakeBookPracticeSet ? "mistake_book_practice_set" : source,
      testDetails,
    });

    history.push(
      resolvedReturnTo,
      isMistakeBookPracticeSet ? mistakeBookReturnState : undefined
    );
  };

  useEffect(() => {
    if (waitingForSubmit) return;

    dispatch(clearBasicAnalytics());
    setAttempt(0);
    dispatch(fetchBasicAnalyticsRequest(getRequestPayload()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isFromSubmit,
    waitingForSubmit,
    examSubmitted,
    routePaperType,
    routeSetId,
    routePaperId,
    routePlannerTestId,
  ]);

  const apiData = basicAnalytics?.data;

  // Keep dropdown bound to the attempt id that owns the current report data.
  useEffect(() => {
    if (!apiData) return;
    const resolved = resolveAttemptId(
      apiData.selected_attempt,
      apiData.attempts || []
    );
    if (resolved === undefined || Number.isNaN(Number(resolved))) return;
    setAttempt(Number(resolved));
    dispatch(setSelectedAttemptId(Number(resolved)));
  }, [apiData, dispatch]);

  const handleAttemptChange = (nextAttempt) => {
    if (nextAttempt === attempt) return;

    setAttempt(nextAttempt);
    dispatch(setSelectedAttemptId(Number(nextAttempt)));

    const payload = {
      attempt_count: Number(nextAttempt),
    };

    if (location.state?.set_id) {
      payload.paper_type = location.state.paper_type;
      payload.set_id = location.state.set_id;
    } else if (location.state?.paper_id) {
      payload.paper_type = location.state.paper_type;
      payload.paper_id = location.state.paper_id;
      payload.planner_test_id = location.state.planner_test_id;
    }

    dispatch(fetchBasicAnalyticsRequest(payload));
  };

  // Preparing while submit finishes, then until analytics payload arrives.
  // Keep summary during later attempt refetches so the page does not flicker.
  const showPreparingScorecard = waitingForSubmit || !apiData;

  if (isFromSubmit && examError && !examSubmitted) {
    return (
      <div className="analytics-error">
        <p>Error submitting exam: {examError}</p>
      </div>
    );
  }

  if (error?.basic && !apiData && !waitingForSubmit) {
    return (
      <div className="analytics-error">
        <p>Error: {error.basic}</p>
      </div>
    );
  }

  if (showPreparingScorecard) {
    return (
      <div className="analytics-wrapper">
        <Header
          paperName={testDetails?.paperName || ""}
          attempts={undefined}
          selectedAttempt={attempt}
          onAttemptChange={handleAttemptChange}
          onBack={handleBack}
        />
        <div className="analytics-loader">
          <ClipLoader color="#C61C26" size={55} />
          <p>Preparing your scorecard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-wrapper">
      <Header
        paperName={apiData?.paper_name}
        attempts={apiData?.attempts}
        selectedAttempt={attempt}
        onAttemptChange={handleAttemptChange}
        onBack={handleBack}
      />
      <BasicReports basicAnalytics={apiData} />
    </div>
  );
};

export default AnalyticsScreen;
